import React from 'react';
import { CheckIcon, XMarkIcon, BoltIcon, SparklesIcon } from '@heroicons/react/20/solid';
import { motion, AnimatePresence } from 'framer-motion';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.insightos.co.uk';

const creditTiers = [
  {
    name: 'Starter',
    subtitle: '150 Credits',
    id: 'tier-150',
    credits: 150,
    price: '£50',
    perCredit: '33p / credit',
    discount: null,
    featured: false,
    features: ['150 lead credits', 'Instant top-up', 'Best for small runs'],
  },
  {
    name: 'Growth',
    subtitle: '300 Credits',
    id: 'tier-300',
    credits: 300,
    price: '£100',
    perCredit: '33p / credit',
    discount: null,
    featured: false,
    features: ['300 lead credits', 'Instant top-up', 'Best for weekly outreach'],
  },
  {
    name: 'Scale',
    subtitle: '1,000 Credits',
    id: 'tier-1000',
    credits: 1000,
    price: '£300',
    perCredit: '30p / credit',
    discount: '10% off',
    featured: true,
    features: ['1,000 lead credits', 'Priority credit processing', '10% pack discount'],
  },
  {
    name: 'Pro',
    subtitle: '2,000 Credits',
    id: 'tier-2000',
    credits: 2000,
    price: '£565',
    perCredit: '28p / credit',
    discount: null,
    featured: false,
    features: ['2,000 lead credits', 'Lower cost per credit', 'Built for team usage'],
  },
  {
    name: 'Business',
    subtitle: '5,000 Credits',
    id: 'tier-5000',
    credits: 5000,
    price: '£1,350',
    perCredit: '27p / credit',
    discount: '15% off',
    featured: false,
    features: ['5,000 lead credits', 'High-volume outreach', '15% pack discount'],
  },
  {
    name: 'Enterprise',
    subtitle: '10,000 Credits',
    id: 'tier-10000',
    credits: 10000,
    price: '£2,500',
    perCredit: '25p / credit',
    discount: '25% off',
    featured: false,
    features: ['10,000 lead credits', 'Maximum scale package', '25% pack discount'],
  },
];

const classNames = (...classes) => classes.filter(Boolean).join(' ');

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.07, delayChildren: 0.15 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 28, scale: 0.97 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { type: 'spring', stiffness: 260, damping: 22 } },
};

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.22 } },
  exit: { opacity: 0, transition: { duration: 0.18 } },
};

const panelVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.97 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { type: 'spring', stiffness: 300, damping: 28, delay: 0.05 } },
  exit: { opacity: 0, y: 24, scale: 0.97, transition: { duration: 0.16 } },
};

export default function CreditBuyModal({ open, onClose, creditsRemaining, creditsUsed, totalLeads }) {
  const [selectedCreditPackId, setSelectedCreditPackId] = React.useState('');
  const [buyingPackageId, setBuyingPackageId] = React.useState(null);
  const [checkoutError, setCheckoutError] = React.useState('');

  // 3 credits enrich 1 lead, so this is the pack that covers every lead they currently have
  const creditsNeededForAllLeads = Math.max(0, Math.ceil((Number(totalLeads) || 0) / 3)) * 3;
  const recommendedTierId = React.useMemo(() => {
    if (!creditsNeededForAllLeads) return null;
    const sufficientTier = [...creditTiers].sort((a, b) => a.credits - b.credits).find((tier) => tier.credits >= creditsNeededForAllLeads);
    return (sufficientTier || creditTiers[creditTiers.length - 1]).id;
  }, [creditsNeededForAllLeads]);

  const packageIdByTierId = React.useMemo(
    () => ({
      'tier-150': 6,
      'tier-300': 7,
      'tier-1000': 8,
      'tier-2000': 9,
      'tier-5000': 10,
      'tier-10000': 11,
    }),
    []
  );

  React.useEffect(() => {
    if (!open) {
      setSelectedCreditPackId('');
      setBuyingPackageId(null);
      setCheckoutError('');
    }
  }, [open]);

  const handleBuyPackage = async (tierId) => {
    const packageId = packageIdByTierId[tierId];
    if (!packageId) {
      setCheckoutError('Unable to start checkout for this package.');
      return;
    }

    setCheckoutError('');
    setSelectedCreditPackId(tierId);
    setBuyingPackageId(packageId);

    try {
      const payload = {
        packageId,
        amount: 1,
        successUrl: window.location.href,
        cancelUrl: window.location.href,
        avenorCompanyCode: null,
      };

      const attemptCheckout = async (endpoint) => {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload),
        });

        const contentType = response.headers.get('content-type') || '';
        let data = null;

        if (contentType.includes('application/json')) {
          data = await response.json();
        } else {
          const text = await response.text();
          data = {
            success: false,
            message: text && text.trim().startsWith('<')
              ? `Endpoint ${endpoint} not found or returned HTML.`
              : text || `Checkout failed at ${endpoint}.`,
          };
        }

        return { response, data };
      };

      const checkoutEndpoints = [
        `${API_BASE}/avenor/avenorcheckout`,
        `${API_BASE}/api/billing/avenorcheckout`,
      ];

      let result = null;
      for (const endpoint of checkoutEndpoints) {
        result = await attemptCheckout(endpoint);
        if (result?.response?.ok && result?.data?.success) break;
      }

      const { response, data } = result || {};
      if (!response || !response.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to create Avenor checkout session.');
      }
      if (!data?.url) throw new Error('Stripe checkout URL not returned.');

      window.location.assign(data.url);
    } catch (err) {
      setCheckoutError(err.message || 'Failed to start checkout.');
      setBuyingPackageId(null);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80] overflow-y-auto"
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-slate-950/75 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Scroll container */}
          <div className="relative min-h-full px-3 py-10 sm:px-6 sm:py-14 lg:px-8">
            <motion.div
              className="relative mx-auto max-w-6xl"
              variants={panelVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {/* Close */}
              <motion.button
                type="button"
                onClick={onClose}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.94 }}
                className="absolute -top-2 right-0 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition hover:bg-white/20"
              >
                <XMarkIcon className="h-5 w-5" />
              </motion.button>

              {/* Header card */}
              <div className="mb-10 overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-700 px-6 py-10 text-center shadow-[0_30px_80px_rgba(99,60,220,0.45)] sm:px-10 sm:py-12">
                {/* Decorative orbs */}
                <div className="pointer-events-none absolute -left-16 -top-16 h-64 w-64 rounded-full bg-white/5 blur-3xl" />
                <div className="pointer-events-none absolute -bottom-12 -right-12 h-56 w-56 rounded-full bg-purple-300/10 blur-3xl" />

                <div className="relative flex items-center justify-center gap-3 mb-5">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 shadow-inner backdrop-blur">
                    <BoltIcon className="h-6 w-6 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-200">Avenor Credits</p>
                    <p className="text-lg font-extrabold text-white leading-tight">Top Up Your Account</p>
                  </div>
                </div>

                <h2 className="relative text-3xl font-extrabold tracking-tight text-white sm:text-5xl">
                  Choose Your Credit Pack
                </h2>
                <p className="relative mx-auto mt-3 max-w-lg text-sm text-indigo-200 sm:text-base">
                  One-off purchases · No subscriptions · Credits never expire
                </p>

                {/* Stats bar */}
                <div className="relative mx-auto mt-7 flex max-w-sm items-center justify-center gap-6 rounded-2xl bg-white/10 px-6 py-3 backdrop-blur">
                  <div className="text-center">
                    <p className="text-xs font-semibold uppercase tracking-wider text-indigo-200">Remaining</p>
                    <p className="text-2xl font-extrabold text-emerald-300">{creditsRemaining ?? '—'}</p>
                  </div>
                  <div className="h-8 w-px bg-white/20" />
                  <div className="text-center">
                    <p className="text-xs font-semibold uppercase tracking-wider text-indigo-200">Used</p>
                    <p className="text-2xl font-extrabold text-amber-300">{creditsUsed ?? '—'}</p>
                  </div>
                  {Number(totalLeads) > 0 && (
                    <>
                      <div className="h-8 w-px bg-white/20" />
                      <div className="text-center">
                        <p className="text-xs font-semibold uppercase tracking-wider text-indigo-200">Leads</p>
                        <p className="text-2xl font-extrabold text-sky-300">{totalLeads}</p>
                      </div>
                    </>
                  )}
                </div>
                {creditsNeededForAllLeads > 0 && (
                  <p className="relative mt-4 text-xs font-semibold text-indigo-200 sm:text-sm">
                    You need ~{creditsNeededForAllLeads} credits to enrich all {totalLeads} leads (3 credits / lead)
                  </p>
                )}
              </div>

              {/* Tier grid */}
              <motion.div
                className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                {creditTiers.map((tier) => {
                  const isLoading = buyingPackageId === packageIdByTierId[tier.id];
                  const isRecommended = tier.id === recommendedTierId;
                  return (
                    <motion.div
                      key={tier.id}
                      variants={cardVariants}
                      whileHover={{ y: -4, transition: { type: 'spring', stiffness: 400, damping: 20 } }}
                      animate={isRecommended ? {
                        boxShadow: [
                          '0 0 0 0 rgba(16,185,129,0.55)',
                          '0 0 0 12px rgba(16,185,129,0)',
                          '0 0 0 0 rgba(16,185,129,0)',
                        ],
                      } : {}}
                      transition={isRecommended ? { duration: 2, repeat: Infinity, ease: 'easeOut' } : undefined}
                      className={classNames(
                        'relative flex flex-col rounded-3xl p-6 sm:p-7',
                        isRecommended && 'ring-2 ring-emerald-400',
                        tier.featured
                          ? 'bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-700 text-white shadow-[0_20px_60px_rgba(99,60,220,0.5)]'
                          : 'border border-slate-200/70 bg-white text-slate-900 shadow-[0_8px_30px_rgba(15,23,42,0.1)]'
                      )}
                    >
                      {/* Popular badge */}
                      {tier.featured && !isRecommended && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-400 px-3 py-0.5 text-xs font-extrabold uppercase tracking-wider text-amber-900 shadow-lg">
                            <SparklesIcon className="h-3 w-3" /> Most Popular
                          </span>
                        </div>
                      )}

                      {/* Best-fit badge for the pack that covers all current leads, pulses to draw attention */}
                      {isRecommended && (
                        <motion.div
                          className="absolute -top-3 left-1/2 -translate-x-1/2"
                          animate={{ scale: [1, 1.08, 1] }}
                          transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                        >
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500 px-3 py-0.5 text-xs font-extrabold uppercase tracking-wider text-white shadow-lg">
                            <BoltIcon className="h-3 w-3" /> Best for Your Leads
                          </span>
                        </motion.div>
                      )}

                      {/* Discount badge */}
                      {tier.discount && !tier.featured && (
                        <div className="absolute right-5 top-5">
                          <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-bold text-emerald-700">
                            {tier.discount}
                          </span>
                        </div>
                      )}

                      <div className="mb-5">
                        <p className={classNames(
                          'text-xs font-bold uppercase tracking-[0.18em] mb-1',
                          tier.featured ? 'text-indigo-200' : 'text-indigo-500'
                        )}>
                          {tier.subtitle}
                        </p>
                        <h3 className={classNames(
                          'text-2xl font-extrabold',
                          tier.featured ? 'text-white' : 'text-slate-900'
                        )}>
                          {tier.name}
                        </h3>
                      </div>

                      <div className="mb-6">
                        <div className="flex items-end gap-2">
                          <span className={classNames(
                            'text-5xl font-extrabold tracking-tight',
                            tier.featured ? 'text-white' : 'text-slate-900'
                          )}>
                            {tier.price}
                          </span>
                        </div>
                        <p className={classNames(
                          'mt-1 text-xs font-semibold',
                          tier.featured ? 'text-indigo-200' : 'text-slate-400'
                        )}>
                          {tier.perCredit}
                          {tier.featured && tier.discount && (
                            <span className="ml-2 rounded-full bg-white/20 px-2 py-0.5 text-white">
                              {tier.discount}
                            </span>
                          )}
                        </p>
                      </div>

                      <ul className="mb-8 flex-1 space-y-2.5">
                        {tier.features.map((feature) => (
                          <li key={feature} className="flex items-center gap-2.5">
                            <span className={classNames(
                              'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full',
                              tier.featured ? 'bg-white/20' : 'bg-indigo-50'
                            )}>
                              <CheckIcon className={classNames(
                                'h-3 w-3',
                                tier.featured ? 'text-white' : 'text-indigo-600'
                              )} />
                            </span>
                            <span className={classNames(
                              'text-sm font-medium',
                              tier.featured ? 'text-indigo-100' : 'text-slate-600'
                            )}>
                              {feature}
                            </span>
                          </li>
                        ))}
                      </ul>

                      <motion.button
                        type="button"
                        onClick={() => handleBuyPackage(tier.id)}
                        disabled={isLoading}
                        whileHover={!isLoading ? { scale: 1.03 } : {}}
                        whileTap={!isLoading ? { scale: 0.97 } : {}}
                        className={classNames(
                          'w-full rounded-2xl py-3 text-sm font-extrabold tracking-wide transition-all disabled:cursor-not-allowed disabled:opacity-60',
                          tier.featured
                            ? 'bg-white text-indigo-700 shadow-[0_4px_20px_rgba(255,255,255,0.3)] hover:bg-indigo-50'
                            : 'bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow-[0_4px_20px_rgba(99,60,220,0.35)] hover:from-indigo-500 hover:to-violet-500'
                        )}
                      >
                        {isLoading ? (
                          <span className="flex items-center justify-center gap-2">
                            <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                            </svg>
                            Redirecting…
                          </span>
                        ) : (
                          'Buy Now'
                        )}
                      </motion.button>
                    </motion.div>
                  );
                })}
              </motion.div>

              {/* Error */}
              <AnimatePresence>
                {checkoutError && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 6 }}
                    className="mx-auto mt-6 max-w-2xl rounded-2xl border border-red-200 bg-red-50 px-5 py-3.5 text-sm font-medium text-red-700 shadow"
                  >
                    {checkoutError}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Footer note */}
              <p className="mt-8 text-center text-xs text-slate-400">
                Secured by <span className="font-semibold text-slate-500">Stripe</span> · All prices include VAT where applicable
              </p>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
